// Imports
import React from 'react';
import styled, { css } from 'styled-components/macro';
import { View } from '..';
import determineMonthlySubscriptionAmount from '../../../../shareables/utils/billing/determine-monthly-subscription-amount';
import availableSubscriptionSizes from '../../../../shareables/utils/billing/available-subscription-sizes';
import planMapping from '../../../../shareables/utils/billing/plan-mapping';
import InfoIconTooltip from '../shared-components/info-icon';


// Styled components
const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.3125rem;
	width: 100%;
	
	@media (max-width: 23rem) {
		gap: 0.75rem;
	}
`;

const PriceContainer = styled.div<{ view: View }>`
	display: flex;
	align-items: center;
	gap: 0.9375rem;
	
	line-height: 1.2;
	
	${({ view }) =>
		view === 'YEARLY'
			? css`
					@media (max-width: 23rem) {
						flex-direction: column;
						width: 100%;
						align-items: start;
						
						gap: 0.3125rem;
					}
				`
			: ''}
	
	small {
		font-size: 75%;
	}
`;

const Price = styled.span`
	display: flex;
	align-items: flex-start;
	
	font-size: 3.75rem;
	font-weight: 600;
	line-height: 1;
	
	@media (max-width: 30rem) {
		font-size: 3rem;
	}
`;

const DollarSign = styled.span`
	font-size: 1.875rem;
	font-weight: 600;
	line-height: 1;
	
	@media (max-width: 30rem) {
		font-size: 1.5rem;
	}
`;


// Define the accepted props
interface Props {
	view: View;
	sizeIndex: number;
	plan: Exclude<keyof typeof planMapping, 'free' | 'deluxe'>;
}


// Function component
const Pricing: React.FC<Props> = ({ view, sizeIndex, plan }) => {
	// Initialize
	const maxInformPlanIndex = availableSubscriptionSizes(planMapping.inform).length - 1;
	
	
	// Return JSX
	return (
		<Wrapper>
			<PriceContainer view={view}>
				<Price>
					{plan === 'engage' || sizeIndex <= maxInformPlanIndex ? (
						<React.Fragment>
							<DollarSign>$</DollarSign>
							{
								determineMonthlySubscriptionAmount({
									plan: planMapping[plan],
									size: availableSubscriptionSizes(planMapping[plan])[sizeIndex],
									yearly: view === 'YEARLY',
								}).value
							}
						</React.Fragment>
					) : (
						'N/A'
					)}
				</Price>
				{plan === 'engage' || sizeIndex <= maxInformPlanIndex ? (
					<span>
						Per month
						{view === 'YEARLY' && (
							<React.Fragment>
								,<br />
								billed yearly
							</React.Fragment>
						)}
						<br />
						<small>
							Up to {availableSubscriptionSizes(planMapping[plan])[sizeIndex].toLocaleString()} households
							<InfoIconTooltip tooltipText='Adjust this using the slider above' />
						</small>
					</span>
				) : (
					`Supports max of ${availableSubscriptionSizes(planMapping[plan])[maxInformPlanIndex].toLocaleString()} households`
				)}
			</PriceContainer>
		</Wrapper>
	);
};

export default Pricing;
